<template>
  <!-- eslint-disable vue/no-v-html -->
  <pre class="p-code" :style="codeStyle"><code v-html="renderedCode"></code></pre>
</template>

<script lang="ts">
  import Vue from 'vue';

  export default Vue.extend({
    props: {
      lang: { type: String, default: undefined },
      dark: { type: Boolean, default: false },
    },
    data: () => ({
      renderedCode: '',
    }),
    computed: {
      codeStyle(): Record<string, string> {
        return {
          'background-color': this.dark ? '#21262d' : '#fefefe',
          color: this.dark ? '#fefefe' : '#21262d',
        };
      },
    },
    beforeUpdate() {
      this.$debounce(() => {
        this.performHighlighting();
      }, 500, this)();
    },
    async mounted() {
      this.performHighlighting();
    },
    methods: {
      highlightJs(value: string): string {
        return value
          .replace(/([^a-zA-Z\\])('|"|`)([a-zA-Z \\_-]*)([^\\]('|"|`)*)/g, '$1<span style="color: #ce9178">$2$3$4</span>') // Strings
          .replace(/(\s)this\.([^\s])/g, '$1<span style="color: #569cd6">this</span>.$2') // This
          .replace(/(\s)(const|let|var|new|async|await|typeof|function|super)(\s)/g, '$1<span style="color: #569cd6">$2</span>$3') // Keywords
          .replace(/(\s|\.)([\w$_]+)(\(.*\))/g, '$1<span style="color: #d2a8ff">$2</span>$3') // Functions
          .replace(/(\s*)(if|else|break;|switch|case|for|while|try|catch|finally)(\s)/g, '$1<span style="color: #c586c0">$2</span>$3') // Keywords flow
          .replace(/(\s\/\/.+[\n])|(\/\*([\n]|.)+\*\/)/g, '<span style="color: #6a9955">$1$2</span>') // Comments
          .replace(/(\s|{)(['"]?\w+['"]?)(:\s*)/g, '$1<span style="color: #79c0ff">$2</span>$3'); // Props
      },
      highlightCSharp(value: string): string {
        return value
          .replace(/([^a-zA-Z\\])('|"|`)([a-zA-Z \\_-]*)([^\\]('|"|`)*)/g, '$1<span style="color: #ce9178">$2$3$4</span>') // Strings
          .replace(/(\s)this\.([^\s])/g, '$1<span style="color: #569cd6">this</span>.$2') // This
          .replace(/(\s*)(string|object|int|var|double|float|void|new|public|protected|sealed|private|namespace|static|async|await|abstract|default|interface|null|long|out)(\s)/g, '$1<span style="color: #569cd6">$2</span>$3') // Keywords
          .replace(/(\s)(#endif|#if|#region)(\s)/gi, '$1<span style="color: #9b9b9b">$2</span>$3') // Pragmas
          .replace(/(\s\/\/.+[\n])|(\/\*([\n]|.)+\*\/)/g, '<span style="color: #6a9955">$1$2</span>') // Comments
          .replace(/(\s*)(if|else|break;|switch|case|for|while|foreach|try|catch|finally|using)(\s|\(|{)/g, '$1<span style="color: #c586c0">$2</span>$3') // Keywords flow
          .replace(/(\s|\.)([\w$_]+)(\(.*\))/g, '$1<span style="color: #b8b87b">$2</span>$3'); // Functions
      },
      higlightJson(value: string): string {
        try {
          return JSON.stringifyToHTML(JSON.parse(value));
        } catch (e1) {
          try {
            return JSON.highlightJSON(value);
          } catch (e2) {
            return value ?? '';
          }
        }
      },
      performHighlighting() {
        const defaultSlot = this._.component.getSlot(this, 'default');
        if (defaultSlot) {
          const slotValue = defaultSlot.text
            ?.replace(/^[ ]*\n/g, '') // Trim leading breaks
            .replace(/\n[ ]*$/g, ''); // Trim trailing breaks

          // Previously used highlight.js, which was huge...
          // for our simple usage this will do plenty
          switch (this.lang?.toLowerCase()) {
          case 'json':
            this.renderedCode = this.higlightJson(slotValue!);
            break;
          case 'csharp':
          case 'c#':
            this.renderedCode = this.highlightCSharp(slotValue!);
            break;
          case 'js':
          case 'javascript':
            this.renderedCode = this.highlightJs(slotValue!);
            break;
          default:
            this.renderedCode = slotValue!;
          }
        }
      },
    },
  });
</script>
